import {CSSProperties, styled} from '@linaria/react';
import React, {PropsWithChildren} from 'react';
import {COLORS} from '../../../constants/colors';
import {Icon} from '../Icon/Icon';
import {GenericIconName} from '../Icon/IconName';

export type OpenerType = 'single-arrow' | 'double-arrow' | 'caret' | null;

interface Props {
  isOpen: boolean;
  isActive?: boolean;
  openerType?: OpenerType;
  onClick?: () => void;
  forceBlackBorderHover?: boolean;
  forceGreyBorder?: boolean;
  forceIconColor?: keyof typeof COLORS;
  className?: string;
  style?: CSSProperties;
}
export const ContentOpener = React.forwardRef<
  HTMLButtonElement,
  PropsWithChildren<Props>
>(function ContentOpener(
  {
    children,
    isOpen,
    isActive,
    onClick,
    openerType,
    forceBlackBorderHover,
    forceGreyBorder,
    forceIconColor,
    ...rest
  },
  ref,
) {
  return (
    <ContentOpenerWrapper
      $isOpen={isOpen}
      $isActive={isActive}
      $forceGreyBorder={forceGreyBorder}
      data-black-border-on-hover={forceBlackBorderHover}
      onClickCapture={() => onClick?.()}
      data-testid="opener"
      role="button"
      {...rest}
      ref={ref}
    >
      {children}
      {openerType && (
        <OpenerArrow
          openerType={openerType}
          isOpen={isOpen}
          isActive={isActive}
          forceIconColor={forceIconColor}
        />
      )}
    </ContentOpenerWrapper>
  );
});

const OpenerArrow: React.FC<{
  openerType: OpenerType;
  isOpen: boolean;
  isActive?: boolean;
  forceIconColor?: keyof typeof COLORS;
}> = ({openerType, isOpen, isActive, forceIconColor}) => {
  switch (openerType) {
    case 'single-arrow':
      return (
        <SingleArrow
          size={'XS'}
          asset={GenericIconName.Dropdown}
          $isOpen={isOpen}
          $isActive={isActive}
          $forceIconColor={forceIconColor}
        />
      );
    case 'caret':
      return (
        <SingleArrow
          size={'XXS'}
          asset={GenericIconName.ArrowDown}
          $isOpen={isOpen}
          $isActive={isActive}
          $forceIconColor={forceIconColor}
        />
      );
    case 'double-arrow':
    default:
      return (
        <DoubleArrow
          size={'XS'}
          asset={GenericIconName.DoubleArrow}
          $isOpen={isOpen}
          $isActive={isActive}
          fill={forceIconColor}
        />
      );
  }
};

const StyledIcon = styled(Icon)<{
  $isOpen: boolean;
  $isActive?: boolean;
  $forceIconColor?: keyof typeof COLORS;
}>`
  display: inline;
  margin-right: 0.5rem;
  opacity: ${({$isOpen, $isActive}) => ($isActive || $isOpen ? 1 : 0.4)};

  color: ${({$forceIconColor}) =>
    $forceIconColor ? `${COLORS[$forceIconColor]}` : 'unset'};
`;

export const ContentOpenerWrapper = styled.button<{
  $isOpen: boolean;
  $isActive?: boolean;
  $forceGreyBorder?: boolean;
  $blackBorderOnHover?: boolean;
}>`
  display: flex;
  align-items: center;
  cursor: pointer;
  border: solid 1px
    ${({$isActive, $forceGreyBorder}) => {
      if ($forceGreyBorder) {
        return COLORS.Grey3;
      }
      return $isActive ? COLORS.Black : COLORS.Grey3;
    }};
  color: ${({$isOpen, $isActive}) =>
    $isActive || $isOpen ? COLORS.Black : COLORS.Grey1};
  background-color: ${COLORS.White};
  border-radius: 4px;
  position: relative;
  padding: 0;
  width: 100%;

  &:hover {
    color: ${COLORS.Black};

    ${StyledIcon} {
      opacity: 1;
    }
  }

  &[data-black-border-on-hover='true'] {
    &:hover {
      border: solid 1px ${COLORS.Black};
    }
  }

  justify-content: space-between;
`;

const SingleArrow = styled(StyledIcon)<{$isOpen: boolean; $isActive?: boolean}>`
  margin-top: 0.2rem;
  transform: ${({$isOpen}) => ($isOpen ? 'scale(-1);' : 'unset')};
`;

const DoubleArrow = styled(StyledIcon)`
  margin-top: 2px;
`;
